import { Constants } from "config/constants";
import {
  checkUnsignedChangesDocsArePresent,
  checkUnsignedDirectorApprovalDocsArePresent,
  checkUnsignedShareholderApprovalDocsArePresent,
} from "utils/unsignedTransactionDocs";

/** Step 1: Application is in DRAFT status */

/** Step 2:  Application is sent to Registrar for review (status: REGISTRAR-REVIEW) */
const handleRegistrarReview = (
  {
    transaction = false,
    unresolvedComments = [],
    legalDepartmentIsPresent = false,
    isApprover = false,
  }
) => {
  const unsignedChangesDocsArePresent =
    checkUnsignedChangesDocsArePresent(transaction);
  const unsignedDirectorApprovalDocsArePresent =
    checkUnsignedDirectorApprovalDocsArePresent(transaction);
  const unsignedShareholderAprrovalDocsArePresent =
    checkUnsignedShareholderApprovalDocsArePresent(transaction);
  let status = "";
  let toastMessage = "";

  if (unresolvedComments?.length > 0) {
    /** If the Registrar left unresolved comments sent back for corrections */
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CORRECTIONS;
    toastMessage = "Application sent back to uploader for corrections";
  } else if (!isApprover) {
    /** If a legal department exists, send the application to the legal department */
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_APPROVAL;
    toastMessage = "Application sent to Registrar for approval";
  } else if (isApprover) {
    if (transaction.transactionType === Constants.TRANSACTION_TYPES.MANUAL) {
      if (
        transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION
      ) {
        if (transaction.fileUploads?.courtOrder) {
          /** Send directly to admin if no documents are present for signature */
          status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW;
          toastMessage = "Application sent to Admin for review";
        } else {
          /** If the transaction is a Notice to Cancel Conversion, should be awaiting court order */
          status =
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.AWAITING_COURT_ORDER;
          toastMessage = "Application sent for court order";
        }
      } else if (
        transaction.changeType === Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF
      ) {
        if (transaction.fileUploads?.noticeOfDissolution) {
          /** Send directly to admin if no documents are present for signature */
          status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW;
          toastMessage = "Application sent to Admin for review";
        } else {
          /** If the transaction is a Strike Off, should be awaiting dissolution notice */
          status =
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.INTENDED_DISSOLUTION;
          toastMessage = "Application sent for intended dissolution";
        }
      } else if (
        transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION
      ) {
        if (transaction.fileUploads?.gazetteNotice) {
          /** Send directly to admin if no documents are present for signature */
          status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW;
          toastMessage = "Application sent to Admin for review";
        } else if (transaction.fileUploads?.letterOfRejection) {
          /** If there is a letter of rejection uploaded, send directly to REJECTED */
          status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REJECTED;
          toastMessage = "Application sent for processing";
        } else {
          /** If the transaction is an Administrative Restoration, should be awaiting affidavit of restoration */
          status =
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
              .PENDING_REGISTRAR_DECISION;
          toastMessage = "Application sent for affidavit of restoration";
        }
      } else {
        /** Send directly to admin if no documents are present for signature */
        status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW;
        toastMessage = "Application sent to Admin for review";
      }
    } else {
      if (legalDepartmentIsPresent) {
        /** If a legal department exists, send the application to the legal department */
        status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_REVIEW;
        toastMessage = "Application sent to legal department for review";
      } else if (unsignedChangesDocsArePresent) {
        /** Send to uploader for signature */
        status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CHANGES_SIGNATURE;
        toastMessage = "Application sent to uploader for official signature";
      } else if (unsignedDirectorApprovalDocsArePresent) {
        /** Send to uploader for director signature */
        status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE;
        toastMessage = "Application sent to uploader for director signature";
      } else if (unsignedShareholderAprrovalDocsArePresent) {
        /** Send to uploader for shareholder signature */
        status =
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_SIGNATURE;
        toastMessage = "Application sent to uploader for shareholder signature";
      } else {
        /** Send directly to admin if no documents are present for signature */
        status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW;
        toastMessage = "Application sent to Admin for review";
      }
    }
  }

  return { status, toastMessage };
};



/** Step 3: handle legal department review (status: LEGAL-REVIEW) */
const handleLegalDepartmentReview = (
  { transaction = false,
    unresolvedComments = false,
    isApprover = false }
) => {
  const unsignedChangesDocsArePresent =
    checkUnsignedChangesDocsArePresent(transaction);
  const unsignedDirectorApprovalDocsArePresent =
    checkUnsignedDirectorApprovalDocsArePresent(transaction);
  const unsignedShareholderAprrovalDocsArePresent =
    checkUnsignedShareholderApprovalDocsArePresent(transaction);
  let status = "";
  let toastMessage = "";

  if ((transaction, unresolvedComments?.length > 0)) {
    /** If legal department left unresolved comments sent back for corrections */
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_CORRECTIONS;
    toastMessage = "Application sent back to uploader for corrections";
  } else if (!isApprover) {
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_APPROVAL;
    toastMessage = "Application sent to legal approval";
  } else if (isApprover) {
    if (unsignedChangesDocsArePresent) {
      /** Send to uploader for signature */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CHANGES_SIGNATURE;
      toastMessage = "Application sent to uploader for official signature";
    } else if (unsignedDirectorApprovalDocsArePresent) {
      /** Send to uploader for director signature */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE;
      toastMessage = "Application sent to uploader for director signature";
    } else if (unsignedShareholderAprrovalDocsArePresent) {
      /** Send to uploader for shareholder signature */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_SIGNATURE;
      toastMessage = "Application sent to uploader for shareholder signature";
    } else {
      /** Send directly to admin if no documents are present for signature */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW;
      toastMessage = "Application sent to Admin for review";
    }
  }

  return { status, toastMessage };
};


/** Step 4: Registrar reviews the signed Changes signature docs (status: REGISTRAR-CS-REVIEW) */
const handleRegistrarCSReview = (
  { transaction = false,
    unresolvedComments = false,
    isApprover = false }
) => {
  const unsignedDirectorApprovalDocsArePresent =
    checkUnsignedDirectorApprovalDocsArePresent(transaction);
  const unsignedShareholderAprrovalDocsArePresent =
    checkUnsignedShareholderApprovalDocsArePresent(transaction);
  let status = "";
  let toastMessage = "";

  if ((transaction, unresolvedComments?.length > 0)) {
    /** If the Registrar left unresolved comments sent back for corrections */
    status =
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_CORRECTIONS;
    toastMessage = "Application sent back to uploader for corrections";
  } else if (!isApprover) {
    /** Send to Registrar for approval */
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_APPROVAL;
    toastMessage = "Application sent to Registrar for approval";
  } else if (isApprover) {
    if (unsignedDirectorApprovalDocsArePresent) {
      /** Send to uploader for director signature */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE;
      toastMessage = "Application sent to uploader for director signature";
    } else if (unsignedShareholderAprrovalDocsArePresent) {
      /** Send to uploader for shareholder signature */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_SIGNATURE;
      toastMessage = "Application sent to uploader for shareholder signature";
    } else {
      /** Send directly to admin if no documents are present for signature */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW;
      toastMessage = "Application sent to Admin for review";
    }
  }

  return { status, toastMessage };
};


/** Step 5: Registrar reviews signed director approval docs (status: REGISTRAR-DR-REVIEW) */
const handleRegistrarDRReview = (
  { transaction = false,
    unresolvedComments = false,
    isApprover = false }
) => {
  const unsignedShareholderAprrovalDocsArePresent =
    checkUnsignedShareholderApprovalDocsArePresent(transaction);
  let status = "";
  let toastMessage = "";

  if ((transaction, unresolvedComments?.length > 0)) {
    /** If the Registrar left unresolved comments sent back for corrections */
    status =
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_CORRECTIONS;
    toastMessage = "Application sent back to uploader for corrections";
  } else if (!isApprover) {
    /** Send to Registrar for approval */
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_APPROVAL;
    toastMessage = "Application sent to uploader for shareholder signature";
  } else if (isApprover) {
    if (unsignedShareholderAprrovalDocsArePresent) {
      /** Send to uploader for shareholder signature */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_SIGNATURE;
      toastMessage = "Application sent to uploader for shareholder signature";
    } else {
      /** Send directly to admin if no documents are present for signature */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW;
      toastMessage = "Application sent to Admin for review";
    }
  }

  return { status, toastMessage };
};



/** Step 6: Registrar reviews signed director approval docs (status: REGISTRAR-SH-REVIEW) */
const handleRegistrarSHReview = (
  { transaction = false,
    unresolvedComments = false,
    isApprover = false }
) => {
  let status = "";
  let toastMessage = "";

  if ((transaction, unresolvedComments?.length > 0)) {
    /** If the Registrar left unresolved comments sent back for corrections */
    status =
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_CORRECTIONS;
    toastMessage = "Application sent back to uploader for corrections";
  } else if (!isApprover) {
    /** Send directly to Registrar for approval */
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_APPROVAL;
    toastMessage = "Application sent to Admin for review";
  } else if (isApprover) {
    /** Send directly to admin if no documents are present for signature */
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW;
    toastMessage = "Application sent to Admin for review";
  }

  return { status, toastMessage };
};



/** Step 7: Handle Reviewing of the application by org-admin (status: ADMIN-REVIEW) */
const handleAdminReview = ({ transaction = false, unresolvedComments = false, isApprover = false }) => {
  let status = "";
  let toastMessage = "";

  if ((transaction, unresolvedComments?.length > 0)) {
    /** If the org-admin left unresolved comments sent back for corrections */
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_CORRECTIONS;
    toastMessage = "Application sent back to Registrar for corrections";
  } else {
    if (isApprover) {
      if (transaction.transactionType === Constants.TRANSACTION_TYPES.MANUAL) {
        /** If the transaction is a Manual transaction, send directly to PROCESSED */
        status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.PROCESSED;
        toastMessage = "Application sent for processing";
        return { status, toastMessage };
      }
      /** Send to registrar to upload signed BRS Forms */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION;
      toastMessage = "Application sent to Registrar for BRS Forms signature";
    } else {
      /** Send to registrar to upload signed BRS Forms */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_APPROVAL;
      toastMessage = "Application sent for approval";
    }
  }

  return { status, toastMessage };
};


/** Step 7: Handle Reviewing of the application by org-admin (status: ADMIN-REVIEW) */
const handleSubmission = ({ transaction = false, unresolvedComments = false, isApprover = false }) => {
  let status = "";
  let toastMessage = "";

  if ((transaction, unresolvedComments?.length > 0)) {
    /** If the org-admin left unresolved comments sent back for corrections */
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_CORRECTIONS;
    toastMessage = "Application sent back to Registrar for corrections";
  } else {
    if (isApprover) {
      /** Send to registrar to upload signed BRS Forms */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_FORM_SIGNATURE;
      toastMessage = "Application sent to Registrar for BRS Forms signature";
    } else {
      /** Send to registrar to upload signed BRS Forms */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION_APPROVAL;
      toastMessage = "Application sent for approval";
    }
  }

  return { status, toastMessage };
};

/** Step 8: super-admin reviews the signed BRS Forms uploaded by the Registrar (status: BRS-REVIEW) */
const handleBRSReview = ({ transaction = false, unresolvedComments = false, isApprover = false }) => {
  let status = "";
  let toastMessage = "";

  if ((transaction, unresolvedComments?.length > 0)) {
    /** If the org-admin left unresolved comments sent back for corrections */
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_CORRECTIONS;
    toastMessage = "Application sent back to Registrar for corrections";
  } else {
    if (isApprover) {
      /** Update status to PROCESSED */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.PROCESSED;
      toastMessage = "Application completed!";
    } else {
      /** Update status to PROCESSED */
      status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_APPROVAL;
      toastMessage = "Application sent for approval";
    }
  }

  return { status, toastMessage };
};


/** Get next status and toast message for a transaction under review */
export const getNextTransactionStatusAndToastMessage = ({
  transaction,
  unresolvedComments = [],
  legalDepartmentIsPresent = false,
  isApprover = false,
  isOrgAdminApprover = false,
  isSuperAdminApprover = false,
  isBrsOpsApprover = false,
}) => {
  switch (transaction.status) {
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_APPROVAL:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW_II:
      return handleRegistrarReview(
        {
          transaction,
          unresolvedComments,
          legalDepartmentIsPresent,
          isApprover
        }
      );

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_APPROVAL:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_REVIEW_II:
      return handleLegalDepartmentReview(
        {
          transaction,
          unresolvedComments,
          isApprover
        }
      );

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_APPROVAL:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_REVIEW_II:
      return handleRegistrarCSReview(
        {
          transaction,
          unresolvedComments,
          isApprover
        }
      );

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_REVIEW_II:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_APPROVAL:
      return handleRegistrarDRReview(
        {
          transaction,
          unresolvedComments,
          isApprover
        }
      );

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_REVIEW_II:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_APPROVAL:
      return handleRegistrarSHReview(
        {
          transaction,
          unresolvedComments,
          isApprover
        }
      );

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW_II:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_APPROVAL:
      return handleAdminReview(
        {
          transaction,
          unresolvedComments,
          isApprover: isOrgAdminApprover
        }
      );

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION_II:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION_APPROVAL:
      return handleSubmission(
        {
          transaction,
          unresolvedComments,
          isApprover: isSuperAdminApprover || isBrsOpsApprover
        }
      );

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_APPROVAL:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW_II:
      return handleBRSReview(
        {
          transaction,
          unresolvedComments,
          isApprover: isSuperAdminApprover || isBrsOpsApprover
        }
      );

    default:
      return handleRegistrarReview(
        {
          transaction,
          unresolvedComments,
          isApprover,
          legalDepartmentIsPresent
        }
      );
  }
};

export const getNextStepLabel = ({
  status,
  isApprover,
  isOrgAdminApprover,
  isSuperAdminApprover,
  isBrsOpsApprover,
}) => {
  switch (status) {
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW_II:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_REVIEW_II:
      if (isApprover) {
        return "Admin";
      } else {
        return "Approver";
      }

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_APPROVAL:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_APPROVAL:
      return "Admin";

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_REVIEW_II:
      if (isApprover) {
        return "Admin";
      } else {
        return "Approver";
      }
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_APPROVAL:
      return "Admin";

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_REVIEW_II:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_REVIEW_II:
      if (isApprover) {
        return "Reviewer";
      } else {
        return "Approver";
      }
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_APPROVAL:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_APPROVAL:
      return "Reviewer";
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW_II:
      if (isOrgAdminApprover) {
        return "Submission";
      }
      return "Approver";
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION_II:
      if (isBrsOpsApprover || isSuperAdminApprover) {
        return "Signature";
      }
      return "Approver";
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW:
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW_II:
      if (isBrsOpsApprover || isSuperAdminApprover) {
        return "Processing";
      }
      return "Approver";
    default:
      return "Reviewer";
  }
};
